import type {
  PowerTransformerResult,
  PowerTransformerObject
} from '../../../..';
import type {
  TransformerEvaluatedLoading,
  TransformerEvaluatedAvailableCapacity,
  TransformerEvaluatedRatedCapacity
} from '../../../../derived-state/loadFlowSummary';
import type { Capacity } from '../../../../domain/Capacity';
import type { ElectricLoad } from '../../../../domain/ElectricLoad';
import { Percent } from '../../../../domain/Percent';
import { RatedCapacity } from '../../../../domain/ratedCapacity';
import type { AttentionStatusType } from '../../../AttentionStatus';
import type {
  ForMessaging,
  ForStoringAttentionStatus
} from '../../../AttentionStatusRepository';
import type { EvaluatedResult } from '../../../EvaluatedResult';
import type { ForPowerTransformerEvaluation } from './forPowerTransformerEvaluation';

export class PowerWithHighestLoadingEvaluation {
  private readonly powerTransformer: PowerTransformerResult;
  private readonly messenger: ForMessaging<AttentionStatusType>;
  private readonly evaluationEngine: ForPowerTransformerEvaluation;

  constructor(
    powerTransformers: PowerTransformerObject[],
    evaluationEngine: ForPowerTransformerEvaluation,
    evaluationStatusRepository: ForStoringAttentionStatus
  ) {
    const powerTransformer =
      getTransformerWithTheHighestLoadingPercent(powerTransformers);
    this.powerTransformer = powerTransformer.result;
    this.evaluationEngine = evaluationEngine;
    this.messenger = evaluationStatusRepository.delegateMessenger(
      powerTransformer.id,
      'PowerTransformer'
    );
  }

  evaluate(): HighestLoadingPowerTransformerEvaluationResult {
    const loadingPercent = this.evaluationEngine.evaluateLoadingPercent(
      Percent.fromFloat(this.powerTransformer.loadingPercent),
      this.messenger
    );
    const availableCapacity = this.evaluationEngine.evaluateAvailableCapacity(
      this.powerTransformer.availableCapacity,
      this.messenger
    );

    const ratedCapacity = this.evaluationEngine.evaluateRatedCapacity(
      RatedCapacity.calculate(
        this.powerTransformer.availableCapacity,
        this.powerTransformer.loadingPercent
      ).value,
      this.messenger
    );

    const attentionRequired = isAttentionRequired(
      loadingPercent,
      availableCapacity,
      ratedCapacity
    );
    return {
      loadingPercent,
      availableCapacity,
      ratedCapacity,
      attentionRequired
    };
  }
}

function isAttentionRequired(
  loadingPercent: TransformerEvaluatedLoading,
  availableCapacity: TransformerEvaluatedAvailableCapacity,
  ratedCapacity: TransformerEvaluatedRatedCapacity
) {
  return (
    loadingPercent.status === 'ATTENTION_REQUIRED' ||
    availableCapacity.status === 'ATTENTION_REQUIRED' ||
    ratedCapacity.status === 'ATTENTION_REQUIRED'
  );
}

function getTransformerWithTheHighestLoadingPercent(
  powerTransformerResults: PowerTransformerObject[]
): PowerTransformerObject {
  const powerTransformerWithHighestLoadingPercent =
    powerTransformerResults.reduce((trafo, current) => {
      return Math.max(
        trafo.result.loadingPercent,
        current.result.loadingPercent
      ) === trafo.result.loadingPercent
        ? trafo
        : current;
    });
  return powerTransformerWithHighestLoadingPercent;
}

export type HighestLoadingPowerTransformerEvaluationResult = {
  loadingPercent: EvaluatedResult<ElectricLoad>;
  availableCapacity: EvaluatedResult<Capacity>;
  ratedCapacity: EvaluatedResult<Capacity>;
  attentionRequired: boolean;
};
